<template>
  <v-dialog
    v-model="dialogOpen"
    v-if="dialogOpen"
    content-class="my-custom-dialog"
    persistent
    max-width="60%"
    scrollable
  >
    <v-card tile color="white">
      <v-card-title>
        <h3 style="text-transform: uppercase" class="text--secondary">
          {{ this.selectedCategoryName }}
        </h3>
        <v-spacer></v-spacer>
        <v-icon large @click="close">mdi-close</v-icon>
      </v-card-title>
      <v-card-text v-if="quantityBarEnabled" class="white pb-0">
        <!-- Quantity Dropdown -->
        <quantity-dropdown
          :selected-quantity="dialogSelectedQuantity"
          :enabled="true"
          @quantity-changed="onDialogQuantityChanged"
        ></quantity-dropdown>
      </v-card-text>
      <v-card-text class="white" :class="quantityBarEnabled ? 'pt-2' : ''">
        <v-container class="panel-container">
          <v-overlay v-if="loading" absolute z-index="10">
            <v-progress-circular
              indeterminate
              size="64"
              color="primary"
            ></v-progress-circular>
          </v-overlay>

          <!-- Search Input - Only for Row Mode -->
          <v-row v-if="displayMode === 'rows'" class="mb-3">
            <v-col cols="12">
              <v-text-field
                ref="searchInput"
                v-model="searchQuery"
                label="Pretraži artikle..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                @click="focusedRowIndex = -1"
                @focus="focusedRowIndex = -1"
                @click:clear="searchQuery = ''"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Tiles Display (Original) -->
          <v-row v-if="displayMode === 'tiles'" class="panel-row">
            <v-col
              v-for="(item, idxc) in sortedProducts[selectedCategoryId]"
              :key="idxc"
              :cols="4"
              class="panel-cell"
              align="center"
            >
              <v-btn
                block
                tile
                depressed
                min-height="6rem"
                :color="item.color"
                return-object
                @click="onCellClick(item)"
                style="display: inline-block; border-radius: 12px; position: relative"
              >
                <span class="white--text tile">{{ item.label }}</span>
                <!-- Keyboard shortcut badge -->
                <v-chip
                  v-if="item.shortcut && keyboardShortcutsEnabled && (showShortcutBadges || showBadgesTemporarily)"
                  x-small
                  color="white"
                  text-color="primary"
                  style="position: absolute; bottom: 4px; right: 4px; font-weight: bold"
                >
                  {{ item.shortcut }}
                </v-chip>
              </v-btn>
            </v-col>
          </v-row>

          <!-- Rows Display (New) -->
          <div v-if="displayMode === 'rows'" class="rows-container">
            <!-- Product Rows -->
            <v-list v-if="filteredProducts.length > 0" class="product-list">
              <template v-for="(item, idx) in filteredProducts">
                <v-list-item
                  :key="idx"
                  :ref="`row-${idx}`"
                  tabindex="0"
                  @click="onRowClick(item, idx)"
                  @focus="focusedRowIndex = idx"
                  :class="['product-row', { 'focused': focusedRowIndex === idx }]"
                  :ripple="true"
                >
                  <v-list-item-content class="py-2">
                    <v-row align="center" no-gutters>
                      <!-- Naziv (Name) -->
                      <v-col cols="4" class="text-left">
                        <div class="product-name text-subtitle-1 font-weight-medium">
                          {{ item.label }}
                        </div>
                      </v-col>

                      <!-- Šifra (Code) -->
                      <v-col cols="1" class="text-center">
                        <div class="product-code text-body-2 grey--text">
                          {{ item.code || '-' }}
                        </div>
                      </v-col>

                      <!-- Cijena (Price) -->
                      <v-col cols="2" class="text-right">
                        <div class="product-price text-subtitle-1 font-weight-bold primary--text">
                          {{ formatPrice(item.price) }}
                        </div>
                      </v-col>

                      <!-- Quantity Badge -->
                      <v-col cols="1" class="text-right">
                        <v-chip
                          v-if="itemQuantities[item.id] > 0"
                          small
                          color="success"
                          text-color="white"
                          class="quantity-badge"
                        >
                          {{ itemQuantities[item.id] }}
                        </v-chip>
                      </v-col>

                      <!-- Kol Input -->
                      <v-col cols="1" class="text-center px-1 pl-3" @click.stop @keydown.stop>
                        <v-text-field
                          :ref="`qtyInput-${idx}`"
                          v-model.number="quantityInputs[item.id]"
                          type="number"
                          min="1"
                          dense
                          outlined
                          hide-details
                          class="qty-input"
                          placeholder="Kol"
                          @click.stop="focusedRowIndex = idx"
                          @focus="focusedRowIndex = idx"
                          @keydown.enter.stop.prevent="setQuantity(item, $event)"
                        ></v-text-field>
                      </v-col>

                      <!-- Mjerna jedinica (Unit) -->
                      <v-col cols="1" class="text-right">
                        <div class="product-unit text-body-2 grey--text">
                          {{ item.unit || 'kom' }}
                        </div>
                      </v-col>

                      <!-- Actions Column -->
                      <v-col cols="2" class="text-left">
                        <div class="action-icons">
                          <!-- Plus icon - always visible -->
                          <v-btn
                            icon
                            small
                            color="success"
                            class="action-btn"
                            aria-label="Dodaj artikl"
                            @click.stop="onIconClick(item, 'add', idx)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>

                          <!-- Minus icon - only if qty > 0 -->
                          <v-btn
                            v-if="itemQuantities[item.id] > 0"
                            icon
                            small
                            color="warning"
                            class="action-btn"
                            aria-label="Umanji količinu"
                            @click.stop="onIconClick(item, 'decrement', idx)"
                          >
                            <v-icon>mdi-minus</v-icon>
                          </v-btn>

                          <!-- Trash icon - only if qty > 0 -->
                          <v-btn
                            v-if="itemQuantities[item.id] > 0"
                            icon
                            small
                            color="error"
                            class="action-btn"
                            aria-label="Ukloni artikl"
                            @click.stop="onIconClick(item, 'removeAll', idx)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="idx < filteredProducts.length - 1" :key="`divider-${idx}`"></v-divider>
              </template>
            </v-list>

            <!-- No Results Message - only show if NOT loading and search query exists and no results -->
            <v-alert v-else-if="!loading && searchQuery && filteredProducts.length === 0" type="info" outlined>
              Nema artikala koji odgovaraju pretrazi "{{ searchQuery }}".
            </v-alert>

            <!-- No Products Message - when category has no products at all (not loading, no search, no products) -->
            <v-alert v-else-if="!loading && !searchQuery && filteredProducts.length === 0" type="warning" outlined>
              Nema artikala u ovoj kategoriji.
            </v-alert>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions class="white"> </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import { clone } from '@/plugins/utils'
import QuantityDropdown from '../register-wide/QuantityDropdown.vue'

export default {
  components: {
    QuantityDropdown
  },
  data () {
    return {
      loading: true,
      dialogOpen: false,
      products: [],
      sortedProducts: {}, // Changed from [] to {} - needs to be object for categoryId keys
      cols: [],
      currency: 'EUR',
      keyBuffer: '',
      keyBufferTimer: null,
      showBadgesTemporarily: false,
      spaceKeyPressed: false,
      spaceKeyPressTimer: null,
      searchQuery: '', // For local search in row mode
      focusedRowIndex: -1, // For keyboard navigation in row mode
      quantityInputs: {}, // Track quantity inputs for each item
      // Dialog-specific quantity management
      dialogSelectedQuantity: 1
    }
  },
  props: {
    selectProductDialogOpen: {
      type: Boolean,
      default: false
    },
    selectedCategoryId: {
      type: String,
      default: ''
    },
    selectedCategoryName: {
      type: String,
      default: ''
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    },
    closeModalOnProductClick: {
      type: Boolean,
      default: false
    },
    displayMode: {
      type: String,
      default: 'tiles',
      validator: (val) => ['tiles', 'rows'].includes(val)
    },
    itemQuantities: {
      type: Object,
      default: () => ({})
    },
    quantityBarEnabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    filteredProducts () {
      // Return empty array only while loading
      if (!this.sortedProducts[this.selectedCategoryId]) {
        return []
      }

      const products = this.sortedProducts[this.selectedCategoryId]

      // If no search query, return all products
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return products
      }

      // Filter by name or code (case-insensitive)
      const query = this.searchQuery.toLowerCase().trim()
      const filtered = products.filter(product => {
        const name = (product.label || '').toLowerCase()
        const code = (product.code || '').toLowerCase()
        return name.includes(query) || code.includes(query)
      })

      // Sort: exact code match first, then others
      return filtered.sort((a, b) => {
        const aCode = (a.code || '').toLowerCase().trim()
        const bCode = (b.code || '').toLowerCase().trim()

        const aExactMatch = aCode === query
        const bExactMatch = bCode === query

        if (aExactMatch && !bExactMatch) return -1
        if (!aExactMatch && bExactMatch) return 1
        return 0
      })
    }
  },
  watch: {
    dialogOpen: {
      handler (nv) {
        // Auto-focus search input when modal opens in row mode
        if (nv && this.displayMode === 'rows') {
          this.$nextTick(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus()
            }
          })
        }
      },
      immediate: true
    },
    selectProductDialogOpen (newValue, _) {
      this.dialogOpen = newValue
      if (this.selectedCategoryId !== '') {
        // Only reset cols if products for this category don't exist yet
        if (!this.sortedProducts[this.selectedCategoryId]) {
          this.cols = []
        }
        this.searchQuery = '' // Reset search when opening modal
        this.focusedRowIndex = -1 // Reset focused row
        this.getProductsByCategory(this.selectedCategoryId)
      }
    },
    searchQuery () {
      // Reset focused row when search changes
      this.focusedRowIndex = -1
    }
  },
  methods: {
    generateShortcut (label) {
      // Split by spaces and get first letter of first 2 words only
      const words = label.trim().split(/\s+/)
      const firstTwo = words.slice(0, 2).map(w => w[0]).filter(Boolean).join('').toUpperCase()
      return firstTwo || 'A' // Fallback to 'A' if empty
    },

    assignShortcuts () {
      const products = this.sortedProducts[this.selectedCategoryId]
      if (!products) return

      const shortcutMap = {}

      products.forEach(product => {
        const baseShortcut = this.generateShortcut(product.label)
        let shortcut = baseShortcut

        // Handle duplicates by adding numbers (max 3 chars total: AB1, AB2... AB9)
        if (shortcutMap[baseShortcut] !== undefined) {
          shortcutMap[baseShortcut]++
          // Limit to single digit to keep max 3 chars
          const count = shortcutMap[baseShortcut]
          if (count <= 9) {
            shortcut = `${baseShortcut}${count}`
          } else {
            // If more than 9 duplicates, just use base + count (will be 4+ chars)
            shortcut = `${baseShortcut}${count}`
          }
        } else {
          shortcutMap[baseShortcut] = 0 // First occurrence
        }

        product.shortcut = shortcut
      })
    },

    handleRowNavigation (e) {
      // Only handle in row mode when dialog is open
      if (!this.dialogOpen || this.displayMode !== 'rows') return

      // Handle plus key for increment (only if shortcuts enabled)
      if (e.key === '+' && this.keyboardShortcutsEnabled && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.filteredProducts[this.focusedRowIndex]
        if (item) {
          this.onIconClick(item, 'add')
        }
        return
      }

      // Handle minus key for decrement (only if shortcuts enabled)
      if (e.key === '-' && this.keyboardShortcutsEnabled && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.filteredProducts[this.focusedRowIndex]
        if (item && this.itemQuantities[item.id] > 0) {
          this.onIconClick(item, 'decrement')
        }
        return
      }

      // Handle X key for remove all (only if shortcuts enabled)
      if ((e.key === 'x' || e.key === 'X') && this.keyboardShortcutsEnabled && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.filteredProducts[this.focusedRowIndex]
        if (item && this.itemQuantities[item.id] > 0) {
          this.onIconClick(item, 'removeAll')
        }
        return
      }

      // Handle arrow keys
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.moveDown()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveUp()
      } else if (e.key === 'Enter' && this.focusedRowIndex >= 0) {
        e.preventDefault()
        const item = this.filteredProducts[this.focusedRowIndex]
        if (item) {
          this.onCellClick(item)
        }
      }
    },

    moveDown () {
      const maxIndex = this.filteredProducts.length - 1
      if (this.focusedRowIndex === -1 && maxIndex >= 0) {
        // If nothing focused, focus first item
        this.focusedRowIndex = 0
        this.scrollToFocusedRow()
        this.focusRow()
      } else if (this.focusedRowIndex < maxIndex) {
        this.focusedRowIndex++
        this.scrollToFocusedRow()
        this.focusRow()
      }
    },

    moveUp () {
      if (this.focusedRowIndex > 0) {
        this.focusedRowIndex--
        this.scrollToFocusedRow()
        this.focusRow()
      } else if (this.focusedRowIndex === 0) {
        // If at first item, move focus back to search
        this.focusedRowIndex = -1
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      }
    },

    focusRow () {
      // Focus the entire row element (v-list-item)
      this.$nextTick(() => {
        const refName = `row-${this.focusedRowIndex}`
        const rowRef = this.$refs[refName]
        if (rowRef && rowRef[0]) {
          rowRef[0].$el.focus()
        }
      })
    },

    scrollToFocusedRow () {
      // Wait for DOM to update and render
      this.$nextTick(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            // The actual scrollable container is .rows-container (has overflow-y: auto)
            const container = document.querySelector('.rows-container')
            if (!container) return

            const focusedElement = document.querySelector('.product-row.focused')
            if (!focusedElement) return

            // Use getBoundingClientRect for reliable positioning
            const containerRect = container.getBoundingClientRect()
            const elementRect = focusedElement.getBoundingClientRect()

            // Calculate relative position of element to container
            const relativeTop = elementRect.top - containerRect.top
            const relativeBottom = elementRect.bottom - containerRect.top

            // Check if element is above visible area
            if (relativeTop < 0) {
              container.scrollTop += relativeTop
            }
            // Check if element is below visible area
            else if (relativeBottom > containerRect.height) {
              container.scrollTop += (relativeBottom - containerRect.height)
            }
          }, 50)
        })
      })
    },

    focusAndSelectSearchInput () {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
          // Select all text in the input
          if (this.$refs.searchInput.$refs.input) {
            this.$refs.searchInput.$refs.input.select()
          }
        }
      })
    },

    handleProductKeyPress (e) {
      // Only handle keys when dialog is open
      if (!this.dialogOpen) return

      // ESC key: Smart behavior based on context
      if (e.key === 'Escape') {
        // Check if we're NOT in an input field, or if we're navigating products
        const inInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)

        if (!inInputField || this.focusedRowIndex >= 0) {
          // First ESC: Focus and select search input (if in row mode)
          if (this.displayMode === 'rows') {
            e.preventDefault()
            this.focusedRowIndex = -1
            this.focusAndSelectSearchInput()
            return
          }
        }

        // Second ESC (or ESC in tiles mode): Close modal
        this.close()
        e.preventDefault()
        return
      }

      // Ignore if typing in input field (except for arrow keys which are handled separately)
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        // Allow arrow keys even in input
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
          return // Let handleRowNavigation handle it
        }
        return
      }

      // Ignore if keyboard shortcuts are disabled
      if (!this.keyboardShortcutsEnabled) return

      // Only handle letters and numbers
      if (!/^[a-zA-Z0-9]$/.test(e.key)) return

      // Clear previous timer
      if (this.keyBufferTimer) {
        clearTimeout(this.keyBufferTimer)
      }

      // Add key to buffer
      this.keyBuffer += e.key.toUpperCase()

      // Set timer to clear buffer after 1 second
      this.keyBufferTimer = setTimeout(() => {
        this.keyBuffer = ''
      }, 1000)

      // Try to find matching product
      const products = this.sortedProducts[this.selectedCategoryId]
      if (!products) return

      const match = products.find(p => p.shortcut === this.keyBuffer)
      if (match) {
        this.onCellClick(match)
        this.keyBuffer = ''
      }
    },

    handleSpaceKeyDown (e) {
      // Only process if it's the Space key and shortcuts are enabled
      if (e.key !== ' ') return
      if (!this.keyboardShortcutsEnabled) return

      // Ignore if user is typing in an input field - check BEFORE preventDefault!
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

      // Only handle when dialog is open
      if (!this.dialogOpen) return

      // If Space key is already pressed, don't start a new timer
      if (this.spaceKeyPressed) return

      // Prevent default space behavior (scrolling) - do this AFTER all checks!
      e.preventDefault()

      // Mark that Space key is pressed
      this.spaceKeyPressed = true

      // Start timer for 600ms - quick enough but distinguishes intentional hold from accidental press
      this.spaceKeyPressTimer = setTimeout(() => {
        // After 600ms, show badges temporarily
        this.showBadgesTemporarily = true
      }, 600)
    },

    handleSpaceKeyUp (e) {
      // Only process if it's the Space key
      if (e.key !== ' ') return

      // Ignore if user is typing in an input field
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

      // Clear the timer if key is released before 600ms
      if (this.spaceKeyPressTimer) {
        clearTimeout(this.spaceKeyPressTimer)
        this.spaceKeyPressTimer = null
      }

      // Hide badges and reset flag
      this.showBadgesTemporarily = false
      this.spaceKeyPressed = false
    },

    formatPrice (price) {
      if (!price) return '-'
      // Price is in cents, divide by 100
      const amount = price / 100
      // Use currency symbol instead of code
      const symbol = this.currency === 'EUR' ? '€' : (this.currency === 'HRK' ? 'kn' : this.currency)
      return `${amount.toFixed(2)} ${symbol}`
    },

    onRowClick (item, idx) {
      // Set focused row when clicking on the row
      this.focusedRowIndex = idx
      // Call the original onCellClick logic
      this.onCellClick(item)
    },

    onCellClick (item) {
      this.$emit('addProduct', item, this.dialogSelectedQuantity)
      // Auto-reset quantity after adding
      this.dialogSelectedQuantity = 1
      // Close modal if setting is enabled
      if (this.closeModalOnProductClick) {
        this.close()
      }
    },

    // Dialog quantity handlers
    onDialogQuantityChanged (quantity) {
      this.dialogSelectedQuantity = quantity
    },

    onIconClick (item, operation, idx) {
      // Set focused row when clicking on an icon
      if (idx !== undefined) {
        this.focusedRowIndex = idx
      }
      // Emit event to parent with item and operation
      this.$emit('modifyProduct', item, operation)
      // Note: Never close modal for icon actions
    },

    setQuantity (item, event) {
      const qty = this.quantityInputs[item.id]

      if (!qty || qty < 1) {
        // Clear invalid input
        this.$set(this.quantityInputs, item.id, null)
        return
      }

      // Emit event with item and quantity
      this.$emit('setProductQuantity', item, qty)

      // Clear input after setting
      this.$nextTick(() => {
        this.$set(this.quantityInputs, item.id, null)
      })

      // Blur (remove focus from) the input field
      if (event && event.target) {
        event.target.blur()
      }
    },

    close () {
      this.keyBuffer = ''
      this.searchQuery = '' // Reset search when closing modal
      this.focusedRowIndex = -1 // Reset focused row
      this.quantityInputs = {} // Clear all quantity inputs
      this.dialogSelectedQuantity = 1 // Reset dialog quantity
      if (this.keyBufferTimer) {
        clearTimeout(this.keyBufferTimer)
      }
      this.$emit('addItemDialogClose')
    },

    async getProductsByCategory (categoryId) {
      if (this.sortedProducts[categoryId]) {
        this.loading = false
        return
      }
      this.loading = true
      if (categoryId === '5692439e-4269-4fd9-875e-71e0e08a4e99') {
        const query = await df.collection(`location_menu_pricelist/${state.getPointOfSale().id}/items`)
          .where('status', '==', 'OK')
          .where('active', '==', true)
        // .where(`prices.${this.currency}.active`, '==', true)
          .get()

        const documents = query.docs.map(doc => doc.data())
        documents.forEach(product => {
          this.cols.push({
            type: 'menu-product',
            color: '#2C5A90',
            id: product.id,
            label: product.name,
            code: product.code || '',
            price: product.prices?.[this.currency]?.price || 0,
            unit: product.selling_unit || 'kom'
          })
        })
      } else {
        const query = await df.collection(`location_pricelist/${state.getPointOfSale().id}/items`)
          .where('category_ids', 'array-contains', `${categoryId}`)
          .where('status', '==', 'OK')
          .where('active', '==', true)
        // .where(`prices.${this.currency}.active`, '==', true)
          .get()

        const documents = query.docs.map(doc => doc.data())
        documents.forEach(product => {
          if (product.warehouse_units && product.warehouse_units.units) {
            Object.keys(product.warehouse_units.units).forEach(key => {
              if (product.warehouse_units.units[key].prices && product.warehouse_units.units[key].prices[this.currency].active === true) {
                let newProduct = {}
                newProduct = clone(product)
                newProduct.prices = product.warehouse_units.units[key].prices

                var stUnit = product.warehouse_units.units[key].storage_unit
                if (stUnit === '.kom') {
                  stUnit = ''
                }
                if (product.warehouse_units.units[key].base_unit === true) {
                  stUnit = product.selling_unit
                }
                newProduct.name = product.name + ' ' + stUnit
                // if (product.warehouse_units.units[key].storage_unit && product.warehouse_units.units[key].storage_unit !== '') {
                //   newProduct.name = product.warehouse_units.units[key].storage_unit
                // } else {
                //   newProduct.name = product.name
                // }
                newProduct.id = newProduct.id + '.' + product.warehouse_units.units[key].id
                this.cols.push({
                  type: 'product',
                  color: '#2C5A90',
                  id: newProduct.id,
                  label: newProduct.name,
                  code: product.code || '',
                  price: newProduct.prices?.[this.currency]?.price || 0,
                  unit: stUnit || 'kom'
                })
              }
            })
          } else {
            this.cols.push({
              type: 'product',
              color: '#2C5A90',
              id: product.id,
              label: product.name,
              code: product.code || '',
              price: product.prices?.[this.currency]?.price || 0,
              unit: product.selling_unit || 'kom'
            })
          }
        })
      }
      // Use $set to ensure Vue reactivity when adding new property to object
      this.$set(this.sortedProducts, categoryId, clone(this.cols.sort((a, b) => a.label.localeCompare(b.label))))
      this.assignShortcuts()
      this.loading = false
    }
  },

  mounted () {
    this.currency = 'HRK'
    if (Date.now() / 1000 > 1672527599) {
      this.currency = 'EUR'
    }
    document.addEventListener('keydown', this.handleProductKeyPress)
    document.addEventListener('keydown', this.handleRowNavigation)
    document.addEventListener('keydown', this.handleSpaceKeyDown)
    document.addEventListener('keyup', this.handleSpaceKeyUp)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.handleProductKeyPress)
    document.removeEventListener('keydown', this.handleRowNavigation)
    document.removeEventListener('keydown', this.handleSpaceKeyDown)
    document.removeEventListener('keyup', this.handleSpaceKeyUp)
    if (this.keyBufferTimer) {
      clearTimeout(this.keyBufferTimer)
    }
    if (this.spaceKeyPressTimer) {
      clearTimeout(this.spaceKeyPressTimer)
    }
  }
}
</script>

<style scoped>
.v-dialog__content >>> .my-custom-dialog {
  position: absolute;
  top: 30px;
  border-radius: 25px;
}
.panel-container {
  margin-top: 1rem;
}
.text-wrap {
  width: min-content;
  margin: auto;
}
.panel-cell {
  padding: 1px;
}
.active {
  background-color: var(--v-accent-darken3);
  color: white;
}
.label {
  font-size: 0.6rem;
  color: white;
}
span.tile {
  display: block;
  white-space: normal;
  max-width: 320px;
  font-weight: 700;
  font-size: 1.4rem;
}

/* Row Display Styles */
.rows-container {
  max-height: 60vh;
  overflow-y: auto;
}

.product-list {
  background: transparent;
}

.product-row {
  cursor: pointer;
  transition: background-color 0.2s, border-left 0.2s;
  border-radius: 8px;
  border-left: 3px solid transparent;
}

.product-row:hover {
  background-color: rgba(33, 150, 243, 0.08);
}

.product-row.focused {
  background-color: rgba(33, 150, 243, 0.15);
  border-left: 3px solid #2196F3;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
}

.product-name {
  color: #333;
  line-height: 1.4;
}

.product-code {
  font-family: monospace;
  font-size: 0.875rem;
}

.product-price {
  color: #2196F3;
}

.product-unit {
  text-transform: uppercase;
  font-size: 0.8rem;
}

.quantity-badge {
  font-weight: bold;
  min-width: 32px;
  justify-content: center;
}

.action-icons {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 0 4px 12px;
  min-width: 144px;
}

.action-btn {
  min-width: 40px;
  min-height: 40px;
  border: 2px solid currentColor;
  border-radius: 4px;
}

.action-btn.v-btn--icon {
  border-radius: 4px !important;
}

/* Qty Input Field */
.qty-input >>> input {
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.qty-input >>> .v-input__control {
  min-height: 36px !important;
}

.qty-input >>> fieldset {
  border-color: #2196F3 !important;
  border-width: 2px !important;
}

.qty-input >>> input:focus {
  background-color: rgba(33, 150, 243, 0.05);
}

/* Hide number input arrows */
.qty-input >>> input[type="number"]::-webkit-inner-spin-button,
.qty-input >>> input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input >>> input[type="number"] {
  -moz-appearance: textfield;
}
</style>
